
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect } from 'react';
import { motion, cubicBezier, AnimatePresence } from 'framer-motion';
import { DiamondIcon, UsersIcon, YouTubeIcon, TwitterIcon, InstagramIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import Portfolio from '../components/ui/Portfolio';
import { testimonials } from '../data/testimonialsData';
import { AppPage, InstagramReel, Tweet, YouTubeVideo } from '../types';
import { CDN_URLS } from '../config';
import { getImageUrl } from '../utils/imageUtils';
import Spinner from '../components/Spinner';
import * as socialMediaService from '@shared/services/socialMediaService';
import SocialCarousel from '../components/SocialCarousel';
import Footer from '../components/Footer';
import { useAppContext } from '../AppContext';
import { useSEO } from '../hooks/useSEO';
import SchemaMarkup from '../components/SchemaMarkup';


interface PortfolioPageProps {
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}


const PortfolioPage: React.FC<PortfolioPageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { userProfile } = useAppContext();

  useSEO(
    t('portfolioPage_seo_title'),
    t('portfolioPage_seo_desc'),
    t('portfolioPage_seo_keywords').split(',').map(k => k.trim())
  );

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  const [isLoadingSocials, setIsLoadingSocials] = useState(true);
  const [reels, setReels] = useState<InstagramReel[]>([]);
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [tweets, setTweets] = useState<Tweet[]>([]);


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonialIndex(prev => (prev + 1) % testimonials.length);
    }, 7000); // Rotate every 7 seconds
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadSocials = async () => {
      try {
        setIsLoadingSocials(true);
        const [reelsData, videosData, tweetsData] = await Promise.all([
          socialMediaService.fetchInstagramReels(),
          socialMediaService.fetchYouTubePlaylist(),
          socialMediaService.fetchTwitterFeed(),
        ]);
        setReels(reelsData);
        setVideos(videosData);
        setTweets(tweetsData);
      } catch (error) {
        console.error("Failed to fetch social media content", error);
      } finally {
        setIsLoadingSocials(false);
      }
    };
    loadSocials();
  }, []);


  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1), staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const testimonialVariants = {
    enter: { opacity: 0, y: 20 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };


  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
      className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 overflow-y-auto custom-scrollbar"
    >
      <div className="max-w-7xl w-full mx-auto">
        <motion.div variants={itemVariants} className="flex flex-col items-center mb-10">
          <DiamondIcon className="w-12 h-12 text-gray-700 dark:text-gray-200 mb-4" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-white text-center leading-tight">
            {t('portfolioPage_title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mt-3 text-center max-w-2xl">
            {t('portfolio.description')}
          </p>
        </motion.div>

        <motion.section variants={itemVariants}>
          <Portfolio />
        </motion.section>

        {/* Testimonials Section */}
        <motion.section variants={sectionVariants} className="mt-16">
          <motion.div variants={itemVariants} className="flex flex-col items-center mb-12">
            <UsersIcon className="w-12 h-12 text-gray-700 dark:text-gray-200 mb-4" />
            <h2 className="text-4xl font-serif font-bold text-gray-900 dark:text-white text-center">
              {t('testimonialsPage_title')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mt-3 text-center max-w-2xl">
              {t('testimonialsPage_tagline')}
            </p>
          </motion.div>
          <div className="relative w-full max-w-3xl mx-auto h-80">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonialIndex}
                variants={testimonialVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700 w-full">
                  <div className="flex items-center mb-4">
                    <img
                      src={getImageUrl(testimonials[currentTestimonialIndex].avatarUrl)}
                      alt={t(testimonials[currentTestimonialIndex].nameKey)}
                      className="w-14 h-14 rounded-full object-cover mr-4 border-2 border-indigo-200 dark:border-indigo-700"
                      loading="lazy"
                      decoding="async"
                    />
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 dark:text-white">{t(testimonials[currentTestimonialIndex].nameKey)}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t(testimonials[currentTestimonialIndex].locationKey)}</p>
                    </div>
                  </div>
                  <blockquote className="text-gray-700 dark:text-gray-300 italic leading-relaxed">
                    "{t(testimonials[currentTestimonialIndex].quoteKey)}"
                  </blockquote>
                  <p className="text-right text-sm font-semibold text-indigo-600 dark:text-indigo-400 mt-4">- {t(testimonials[currentTestimonialIndex].serviceKey)}</p>
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonialIndex(index)}
                  aria-label={`View testimonial ${index + 1}`}
                  className={`w-3 h-3 rounded-full transition-colors ${currentTestimonialIndex === index ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'}`}
                />
              ))}
            </div>
          </div>
        </motion.section>

        {/* Work in Action Section */}
        <motion.section variants={sectionVariants} className="text-center pt-24">
          <h2 className="text-3xl font-serif font-semibold text-gray-900 dark:text-white mb-4">{t('testimonialsPage_workInAction_title')}</h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-8">{t('testimonialsPage_workInAction_tagline')}</p>

          {isLoadingSocials ? (
            <div className="flex justify-center items-center py-12">
              <Spinner className="w-8 h-8 text-indigo-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8 text-left">
              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-3"><YouTubeIcon className="text-red-500" /> {t('testimonialsPage_workInAction_youtubeTitle')}</h3>
                <div className="space-y-4">
                  {videos.map(video => (
                    <div key={video.id} className="flex items-start gap-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/50">
                      <img
                        src={getImageUrl(video.thumbnailUrl)}
                        alt={video.titleKey ? t(video.titleKey) : video.title}
                        className="w-24 h-16 object-cover rounded-md flex-shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                      <div>
                        <p className="font-semibold text-sm line-clamp-2">{video.titleKey ? t(video.titleKey) : video.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{video.channel} &bull; {t('portfolio_video_views', { views: video.views })} &bull; {video.publishedKey ? t(video.publishedKey) : video.publishedAt}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={itemVariants}>
                {reels.length > 0 ? (
                  <SocialCarousel
                    reels={reels}
                    title={t('testimonialsPage_workInAction_instagramTitle')}
                    icon={<InstagramIcon />}
                  />
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700 flex flex-col items-center text-center">
                    <InstagramIcon className="w-12 h-12 text-pink-600 mb-4" />
                    <h3 className="font-bold mb-2">{t('portfolio_instagram_connect_title')}</h3>
                    <p className="text-sm text-gray-500 mb-6">{t('portfolio_instagram_connect_desc')}</p>
                    <button
                      onClick={() => onNavigate('profile')}
                      className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                    >
                      {t('common_connect')}
                    </button>
                  </div>
                )}
              </motion.div>

              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-3"><TwitterIcon /> {t('testimonialsPage_workInAction_twitterTitle')}</h3>
                <div className="space-y-4">
                  {tweets.map(tweet => (
                    <div key={tweet.id} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                      <img
                        src={getImageUrl(tweet.avatarUrl || '')}
                        alt={tweet.authorKey ? t(tweet.authorKey) : tweet.author}
                        className="w-10 h-10 rounded-full flex-shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-sm">{tweet.authorKey ? t(tweet.authorKey) : tweet.author}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{tweet.handle} &bull; {tweet.timestampKey ? t(tweet.timestampKey) : tweet.timestamp}</p>
                        </div>
                        <p className="text-sm mt-1">{tweet.textKey ? t(tweet.textKey) : tweet.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          )}
        </motion.section>
      </div>
      <SchemaMarkup
        type="VideoObject"
        data={videos.map(video => ({
          "@type": "VideoObject",
          name: video.titleKey ? t(video.titleKey) : video.title,
          description: video.titleKey ? t(video.titleKey) : video.title,
          thumbnailUrl: getImageUrl(video.thumbnailUrl),
          uploadDate: video.publishedKey ? t(video.publishedKey) : video.publishedAt,
          author: {
            "@type": "Person",
            name: video.channel
          }
        }))}
      />
      <Footer onNavigate={onNavigate} />
    </motion.div>
  );
};

export default PortfolioPage;
